import { DimensionBreakdownProps } from '@/types/components'
import { DimensionScores } from '@/types'
import { TrendingUp, TrendingDown, Minus, Info, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'

const DIMENSION_COLORS = {
  legal: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300',
    progress: 'bg-green-500'
  },
  financial: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    progress: 'bg-blue-500'
  },
  operational: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-700 dark:text-yellow-300',
    progress: 'bg-yellow-500'
  },
  market: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-300',
    progress: 'bg-purple-500'
  }
}

const DIMENSION_LABELS = {
  legal: 'Legal Compliance',
  financial: 'Financial Health',
  operational: 'Operational Excellence',
  market: 'Market Presence'
}

const TREND_ICONS = {
  improving: TrendingUp,
  declining: TrendingDown,
  stable: Minus
}

const TREND_COLORS = {
  improving: 'text-green-600 dark:text-green-400',
  declining: 'text-red-600 dark:text-red-400',
  stable: 'text-gray-500 dark:text-gray-400'
}

export function DimensionBreakdown({
  dimensions,
  showTrends = true,
  showDetails = true,
  interactive = false,
  className
}: DimensionBreakdownProps) {
  const dimensionEntries = Object.entries(dimensions) as [keyof DimensionScores, DimensionScores[keyof DimensionScores]][]

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
      className
    )}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Dimension Breakdown
        </h3>
        {showDetails && (
          <button className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1">
            View Details
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        {dimensionEntries.map(([key, dimension]) => {
          const colors = DIMENSION_COLORS[key]
          const TrendIcon = TREND_ICONS[dimension.trend]
          const trendColor = TREND_COLORS[dimension.trend]

          return (
            <div
              key={key}
              className={cn(
                'p-4 rounded-lg border transition-all duration-200',
                colors.bg,
                colors.border,
                interactive && 'hover:shadow-md cursor-pointer'
              )}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className={cn('font-medium', colors.text)}>
                    {DIMENSION_LABELS[key]}
                  </h4>
                  {showTrends && (
                    <div className="flex items-center gap-1">
                      <TrendIcon className={cn('w-4 h-4', trendColor)} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn('text-lg font-bold', colors.text)}>
                    {dimension.score}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (Weight: {(dimension.weight * 100).toFixed(0)}%)
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={cn('h-3 rounded-full transition-all duration-500', colors.progress)}
                    style={{ width: `${dimension.score}%` }}
                  />
                </div>
              </div>

              {/* Explanation */}
              {showDetails && (
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {dimension.explanation}
                  </p>
                </div>
              )}

              {/* Verification Points Count */}
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {dimension.verificationPoints?.length || 0} verification points
                </span>
                <span>
                  Updated: {new Date(dimension.lastUpdated).toLocaleDateString()}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            Overall weighted score:
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {dimensionEntries.reduce((acc, [, dim]) => acc + (dim.score * dim.weight), 0).toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  )
}